import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { api } from "../../api/client";
import { btnPrimary, type as text, theme } from "../../theme";
import { SheetCard, StatusTag } from "../../components/Drawing";
import { LoadingBlock } from "../../components/Spinner";
import { NextSteps } from "../../components/NextSteps";
import { EntityTagsRow } from "../../components/EntityTags";
import { useDeepLinkScroll } from "../../hooks/useDeepLinkScroll";
import { Check, EmptyState, Field, ModalHeader, ModalOverlay, input, textarea, secondaryBtn, dangerBtn, toDateInput, dateInputToIso, fmtDateOnly } from "../resilience/shared";
import { AttentionList, Meta, OwnerField, REVIEW_INTERVALS, attentionFor, msRef, personName, useMembers, useMsPermissions, words, type Person } from "./shared";
import { attempt, useClearErrorOnDialog, useMsOverview, useRegister } from "./useRegister";

interface Aspect {
  id: string; displayId: number | null; activity: string; aspect: string; impact: string; condition: string; lifecycleStage: string | null;
  severity: number; likelihood: number; legalRequirement: boolean; interestedPartyConcern: boolean; significanceOverride: boolean | null; overrideReason: string | null;
  controls: string | null; ownerUserId: string | null; owner: Person | null; ownerName: string | null; status: string; reviewIntervalMonths: number; nextReviewAt: string | null;
  significance: { score: number; significant: boolean; reasons: string[]; overridden: boolean };
}
interface Vocabulary { conditions: string[]; lifecycleStages: string[]; significanceThreshold: number }

type Form = Omit<Aspect, "id" | "displayId" | "owner" | "significance" | "nextReviewAt" | "lifecycleStage" | "controls" | "overrideReason" | "ownerUserId" | "ownerName"> & {
  lifecycleStage: string; controls: string; overrideReason: string; ownerUserId: string; ownerName: string; nextReviewAt: string; override: "derived" | "yes" | "no";
};
const blank: Form = {
  activity: "", aspect: "", impact: "", condition: "normal", lifecycleStage: "", severity: 3, likelihood: 3, legalRequirement: false, interestedPartyConcern: false,
  significanceOverride: null, overrideReason: "", controls: "", ownerUserId: "", ownerName: "", status: "active", reviewIntervalMonths: 12, nextReviewAt: "", override: "derived",
};

export function AspectsRegister() {
  const { data, loading, error, reload } = useRegister<{ aspects: Aspect[]; vocabulary: Vocabulary }>("/management-system/aspects");
  const overview = useMsOverview();
  const members = useMembers();
  const { canWrite } = useMsPermissions();
  const [params] = useSearchParams();
  const [editing, setEditing] = useState<{ id: string | null; form: Form } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  useDeepLinkScroll(params.get("aspect"), !!data && !overview.loading);
  useClearErrorOnDialog(setFormError, !!editing);

  if (loading) return <LoadingBlock />;
  if (error || !data) return <p style={{ ...text.body, color: theme.color.dangerText }}>{error}</p>;
  const refresh = () => Promise.all([reload(), overview.reload()]);

  const save = async () => {
    if (!editing) return;
    const { override, ...f } = editing.form;
    const body = {
      ...f, lifecycleStage: f.lifecycleStage || null, controls: f.controls || null, ownerUserId: f.ownerUserId || null, ownerName: f.ownerName || null,
      significanceOverride: override === "derived" ? null : override === "yes", overrideReason: override === "derived" ? null : f.overrideReason || null,
      nextReviewAt: dateInputToIso(f.nextReviewAt),
    };
    if (await attempt(() => (editing.id ? api.put(`/management-system/aspects/${editing.id}`, body) : api.post("/management-system/aspects", body)), setFormError, "Could not save the aspect")) {
      setEditing(null);
      await refresh();
    }
  };
  const act = async (path: string, method: "post" | "del", msg: string) => {
    if (await attempt(() => (method === "post" ? api.post(path) : api.del(path)), setFormError, msg)) await refresh();
  };

  const f = editing?.form;
  const set = (patch: Partial<Form>) => editing && setEditing({ ...editing, form: { ...editing.form, ...patch } });
  const live = data.aspects.filter((a) => a.status === "active");
  const archived = data.aspects.filter((a) => a.status !== "active");

  return (
    <>
      <AttentionList items={attentionFor(overview.data?.attention ?? [], "ms_aspect")} />
      {formError && !editing && <p style={{ ...text.body, color: theme.color.dangerText }}>{formError}</p>}
      <SheetCard label="Aspects and impacts" reference="ISO 14001 6.1.2" actions={canWrite ? <button type="button" style={secondaryBtn} onClick={() => setEditing({ id: null, form: blank })}><Plus size={12} /> Aspect</button> : undefined}>
        <p style={{ ...text.hint, marginTop: 0 }}>
          Significant when severity × likelihood is {data.vocabulary.significanceThreshold} or more, severity is 5, a compliance obligation attaches, or interested parties are concerned. An override is recorded with its reason.
        </p>
        {data.aspects.length === 0 ? (
          <EmptyState
            title="No environmental aspects recorded"
            detail="List the aspects of your activities, products and services you can control or influence — emissions, waste, energy, water, materials — under normal, abnormal and emergency conditions, from a life-cycle perspective."
            action={canWrite ? <button type="button" style={btnPrimary} onClick={() => setEditing({ id: null, form: blank })}>Add an aspect</button> : undefined}
          />
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {[...live, ...archived].map((a) => (
              <div key={a.id} data-deeplink={a.id} style={{ border: "1px solid var(--c-border)", borderRadius: 3, padding: "10px 12px", background: "var(--c-panel)", opacity: a.status === "active" ? 1 : 0.6 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                  <span style={{ ...text.ref, color: theme.color.ref }}>{msRef("aspect", a.displayId)}</span>
                  <span style={text.itemTitle}>{a.aspect}</span>
                  <span style={text.meta}>{a.activity}</span>
                  <StatusTag tone={a.significance.significant ? "gap" : "met"} title={a.significance.reasons.join("; ")}>{a.significance.significant ? "Significant" : "Not significant"} · {a.significance.score}</StatusTag>
                  {a.significance.overridden && <span style={text.meta}>overridden</span>}
                  {canWrite && (
                    <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                      <button type="button" style={secondaryBtn} onClick={() => void act(`/management-system/aspects/${a.id}/reviewed`, "post", "Could not record the review")}>Mark reviewed</button>
                      <button type="button" style={secondaryBtn} onClick={() => setEditing({ id: a.id, form: {
                        activity: a.activity, aspect: a.aspect, impact: a.impact, condition: a.condition, lifecycleStage: a.lifecycleStage ?? "", severity: a.severity, likelihood: a.likelihood,
                        legalRequirement: a.legalRequirement, interestedPartyConcern: a.interestedPartyConcern, significanceOverride: a.significanceOverride, overrideReason: a.overrideReason ?? "",
                        controls: a.controls ?? "", ownerUserId: a.ownerUserId ?? "", ownerName: a.ownerName ?? "", status: a.status, reviewIntervalMonths: a.reviewIntervalMonths,
                        nextReviewAt: toDateInput(a.nextReviewAt), override: a.significanceOverride == null ? "derived" : a.significanceOverride ? "yes" : "no",
                      } })}>Edit</button>
                      <button type="button" style={dangerBtn} onClick={() => window.confirm(`Delete ${msRef("aspect", a.displayId)}?`) && void act(`/management-system/aspects/${a.id}`, "del", "Could not delete the aspect")}>Delete</button>
                    </span>
                  )}
                </div>
                <p style={{ ...text.body, margin: "4px 0", whiteSpace: "pre-line" }}>{a.impact}</p>
                <Meta>
                  <span>Condition: {words(a.condition)}</span>
                  {a.lifecycleStage && <span>Life-cycle stage: {words(a.lifecycleStage)}</span>}
                  <span>Severity {a.severity} · likelihood {a.likelihood}</span>
                  <span>Controls: {a.controls || "none recorded"}</span>
                  <span>Owner: {personName(a.owner, a.ownerName)}</span>
                  <span>Next review: {fmtDateOnly(a.nextReviewAt)}</span>
                </Meta>
                <EntityTagsRow entity={{ kind: "ms_aspect", id: a.id }} readOnly={!canWrite} style={{ marginTop: 6 }} />
              </div>
            ))}
          </div>
        )}
      </SheetCard>

      {editing && f && (
        <ModalOverlay onClose={() => setEditing(null)}>
          <ModalHeader title={editing.id ? "Edit aspect" : "Add environmental aspect"} onClose={() => setEditing(null)} />
          <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {/* Ticks as the form is filled in: what ISO 14001 6.1.2 expects of each aspect. */}
            <div style={{ gridColumn: "1 / -1" }}>
              <NextSteps items={[
                { done: !!f.activity.trim(), label: "Name the activity, product or service" },
                { done: !!f.aspect.trim(), label: "Name the aspect" },
                { done: !!f.impact.trim(), label: "Describe the environmental impact" },
                { done: !!f.lifecycleStage, label: "Say which life-cycle stage it arises in", hint: "ISO 14001 6.1.2 asks for a life-cycle perspective." },
                { done: !!f.controls.trim(), label: "Record the controls and planned action" },
                { done: !!(f.ownerUserId || f.ownerName.trim()), label: "Name an owner" },
                ...(f.override !== "derived" ? [{ done: !!f.overrideReason.trim(), label: "Give the reason for the significance override" }] : []),
              ]} />
            </div>
            <Field label="Activity, product or service"><input style={input} maxLength={300} value={f.activity} onChange={(e) => set({ activity: e.target.value })} /></Field>
            <Field label="Aspect"><input style={input} maxLength={300} value={f.aspect} onChange={(e) => set({ aspect: e.target.value })} placeholder="e.g. Electricity use" /></Field>
            <Field label="Environmental impact" span2><textarea style={textarea} rows={2} value={f.impact} onChange={(e) => set({ impact: e.target.value })} /></Field>
            <Field label="Condition">
              <select style={input} value={f.condition} onChange={(e) => set({ condition: e.target.value })}>{data.vocabulary.conditions.map((c) => <option key={c} value={c}>{words(c)}</option>)}</select>
            </Field>
            <Field label="Life-cycle stage">
              <select style={input} value={f.lifecycleStage} onChange={(e) => set({ lifecycleStage: e.target.value })}>
                <option value="">Not stated</option>
                {data.vocabulary.lifecycleStages.map((c) => <option key={c} value={c}>{words(c)}</option>)}
              </select>
            </Field>
            <Field label="Severity (1–5)"><input type="number" min={1} max={5} style={input} value={f.severity} onChange={(e) => set({ severity: Number(e.target.value) })} /></Field>
            <Field label="Likelihood (1–5)"><input type="number" min={1} max={5} style={input} value={f.likelihood} onChange={(e) => set({ likelihood: Number(e.target.value) })} /></Field>
            <div style={{ gridColumn: "1 / -1", display: "grid", gap: 6 }}>
              <Check checked={f.legalRequirement} onChange={(v) => set({ legalRequirement: v })} label="A compliance obligation attaches to it" />
              <Check checked={f.interestedPartyConcern} onChange={(v) => set({ interestedPartyConcern: v })} label="Interested parties have raised it" />
            </div>
            <Field label="Significance">
              <select style={input} value={f.override} onChange={(e) => set({ override: e.target.value as Form["override"] })}>
                <option value="derived">From the criteria</option>
                <option value="yes">Significant (override)</option>
                <option value="no">Not significant (override)</option>
              </select>
            </Field>
            {f.override !== "derived" && <Field label="Reason for the override"><input style={input} value={f.overrideReason} onChange={(e) => set({ overrideReason: e.target.value })} /></Field>}
            <Field label="Controls and planned action" span2><textarea style={textarea} rows={2} value={f.controls} onChange={(e) => set({ controls: e.target.value })} /></Field>
            <OwnerField userId={f.ownerUserId} name={f.ownerName} members={members} onChange={(o) => set({ ownerUserId: o.userId, ownerName: o.name })} />
            <Field label="Status">
              <select style={input} value={f.status} onChange={(e) => set({ status: e.target.value })}><option value="active">Active</option><option value="archived">Archived</option></select>
            </Field>
            <Field label="Review every">
              <select style={input} value={f.reviewIntervalMonths} onChange={(e) => set({ reviewIntervalMonths: Number(e.target.value) })}>{REVIEW_INTERVALS.map((m) => <option key={m} value={m}>{m} months</option>)}</select>
            </Field>
            <Field label="Next review"><input type="date" style={input} value={f.nextReviewAt} onChange={(e) => set({ nextReviewAt: e.target.value })} /></Field>
            {formError && <p style={{ gridColumn: "1 / -1", margin: 0, ...text.body, color: theme.color.dangerText }}>{formError}</p>}
            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" style={secondaryBtn} onClick={() => setEditing(null)}>Cancel</button>
              <button type="button" style={btnPrimary} disabled={!f.activity.trim() || !f.aspect.trim() || !f.impact.trim()} onClick={() => void save()}>Save</button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </>
  );
}
